import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Archive,
  Boxes,
  Building2,
  ChevronDown,
  ClipboardList,
  ExternalLink,
  Layers,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  ScrollText,
  Search,
  Settings,
  Shield,
  ShoppingBag,
  Tags,
  Truck,
  UserRound,
  Users,
  Warehouse,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useStaff } from "@/context/StaffAuth";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { DemoBadge } from "@/components/admin/AdminUI";

const NAV = [
  { group: "Overview", items: [{ to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, id: "admin-nav-dashboard" }] },
  {
    group: "Catalog",
    perm: "catalog.read",
    items: [
      { to: "/admin/products", label: "Products", icon: Package, id: "admin-nav-products" },
      { to: "/admin/brands", label: "Brands", icon: Tags, id: "admin-nav-brands" },
      { to: "/admin/categories", label: "Categories", icon: Layers, id: "admin-nav-categories" },
      { to: "/admin/collections", label: "Collections", icon: Archive, id: "admin-nav-collections" },
    ],
  },
  {
    group: "Inventory",
    perm: "inventory.read",
    items: [
      { to: "/admin/inventory", label: "Stock & movements", icon: Boxes, id: "admin-nav-inventory" },
      { to: "/admin/warehouses", label: "Warehouses", icon: Warehouse, id: "admin-nav-warehouses" },
    ],
  },
  {
    group: "Purchasing",
    perm: "purchasing.read",
    items: [
      { to: "/admin/purchase-orders", label: "Purchase orders", icon: ClipboardList, id: "admin-nav-purchase-orders" },
      { to: "/admin/suppliers", label: "Suppliers", icon: Truck, id: "admin-nav-suppliers" },
    ],
  },
  { group: "Customers", perm: "customers.read", items: [{ to: "/admin/customers", label: "Customers", icon: UserRound, id: "admin-nav-customers" }] },
  {
    group: "Control",
    items: [
      { to: "/admin/users", label: "Staff users", icon: Users, id: "admin-nav-users", perm: "users.read" },
      { to: "/admin/roles", label: "Roles & permissions", icon: Shield, id: "admin-nav-roles", perm: "users.read" },
      { to: "/admin/audit", label: "Audit log", icon: ScrollText, id: "admin-nav-audit", perm: "audit.read" },
      { to: "/admin/settings", label: "Settings", icon: Settings, id: "admin-nav-settings", perm: "settings.read" },
    ],
  },
];

const SidebarNav = ({ can, onNavigate }) => (
  <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6" data-testid="admin-sidebar-nav">
    {NAV.map((g) => {
      if (g.perm && !can(g.perm)) return null;
      const items = g.items.filter((i) => !i.perm || can(i.perm));
      if (!items.length) return null;
      return (
        <div key={g.group}>
          <p className="eyebrow px-3 mb-2">{g.group}</p>
          <ul className="space-y-0.5">
            {items.map((i) => (
              <li key={i.to}>
                <NavLink
                  to={i.to}
                  end={i.end}
                  onClick={onNavigate}
                  data-testid={i.id}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-foreground/75 hover:text-foreground hover:bg-background transition-colors",
                      isActive && "bg-background text-foreground font-semibold shadow-[0_1px_0_hsl(var(--shadow-1))]"
                    )
                  }
                >
                  <i.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                  <span className="truncate">{i.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      );
    })}
  </nav>
);

const Brand = () => (
  <Link to="/admin" className="flex items-center gap-3 px-5 h-16 border-b" data-testid="admin-brand-link">
    <span className="h-8 w-8 rounded-md bg-foreground text-background flex items-center justify-center font-display text-lg leading-none">É</span>
    <span className="min-w-0">
      <span className="block font-display text-xl leading-none">Maison Éclat</span>
      <span className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">Control center</span>
    </span>
  </Link>
);

export default function AdminLayout() {
  const { user, roles, loading, logout, can } = useStaff();
  const navigate = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [demo, setDemo] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login", { replace: true, state: { from: loc.pathname } });
  }, [loading, user, navigate, loc.pathname]);

  useEffect(() => {
    api.get("/store/config").then((r) => setDemo(!!r.data?.demo_catalog)).catch(() => {});
  }, []);

  const initials = useMemo(() => (user?.name || user?.email || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase(), [user]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background flex" data-testid="admin-loading">
        <div className="hidden lg:block w-72 border-r bg-secondary p-5 space-y-4">
          <Skeleton className="h-8 w-40" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-5/6" /><Skeleton className="h-4 w-4/6" />
        </div>
        <div className="flex-1 p-10 space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-40 w-full" /></div>
      </div>
    );
  }

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/admin/products?q=${encodeURIComponent(q.trim())}`);
    setQ("");
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-shell">
      <aside className="hidden lg:flex lg:w-72 lg:flex-col border-r bg-secondary sticky top-0 h-screen" data-testid="admin-sidebar">
        <Brand />
        <SidebarNav can={can} />
        <div className="border-t px-5 py-4 text-[11px] text-muted-foreground">
          <p className="font-medium text-foreground/80">Luxury Commerce OS</p>
          <p>Phase 1–3 · EUR base currency</p>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-40 h-16 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85 border-b flex items-center gap-3 px-4 sm:px-6 lg:px-8" data-testid="admin-topbar">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open navigation" data-testid="admin-mobile-nav-button"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-80 bg-secondary">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <Brand />
              <SidebarNav can={can} onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          <form onSubmit={submitSearch} className="relative flex-1 max-w-xl" role="search">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products, SKUs…"
              className="pl-9 h-10 bg-card rounded-lg"
              data-testid="admin-command-search-input"
            />
          </form>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            {demo && <span data-testid="admin-environment-badge"><DemoBadge /></span>}
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex text-xs">
              <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-storefront-link"><ExternalLink className="h-3.5 w-3.5 mr-1.5" />Storefront</a>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full border bg-card pl-1 pr-2.5 py-1 hover:bg-secondary transition-colors" data-testid="admin-user-menu-button">
                  <span className="h-7 w-7 rounded-full bg-foreground text-background text-[11px] font-semibold flex items-center justify-center">{initials}</span>
                  <span className="hidden md:block text-xs font-medium max-w-[140px] truncate">{user.name || user.email}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="font-normal">
                  <p className="text-sm font-semibold truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate" data-testid="admin-user-email">{user.email}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">{(roles || []).map((r) => r.name || r.key || r).join(", ") || "No role"}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><a href="/" target="_blank" rel="noreferrer"><ShoppingBag className="h-4 w-4 mr-2" />View storefront</a></DropdownMenuItem>
                {can("settings.read") && <DropdownMenuItem onSelect={() => navigate("/admin/settings")}><Building2 className="h-4 w-4 mr-2" />Store settings</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout-button"><LogOut className="h-4 w-4 mr-2" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
          <div className="max-w-[1440px] mx-auto" key={loc.pathname}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
